import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from '../api/Axios';

const ProviderDragDropUpload = ({ 
  documentType, 
  title, 
  onDocumentUploaded, 
  onDocumentUploadFailed, 
  uploadedFilenames,
  isUploading,
  onUploadStart,
  hasError 
}) => {
  const [localUploading, setLocalUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Get user id from localStorage
  const userId = localStorage.getItem("user_id");

  // Map document types to display names
  const getDocumentTypeDisplayName = useCallback((docType) => {
    const typeMap = {
      'id_passport': 'Photo of Identity/Passport Document',
      'company_registration': 'Company Registration Certificate',
      'business_certificate': 'Business Certificate',
      'proof_of_account': 'Proof of Bank Account'
    };
    return typeMap[docType] || 'Document';
  }, []);

  const uploadFile = useCallback(async (file) => {
    if (!userId) {
      setUploadError("User not logged in.");
      return;
    }

    // Validate file size (12MB limit)
    const maxSizeInBytes = 12 * 1024 * 1024; // 12MB
    if (file.size > maxSizeInBytes) {
      const errorMsg = `File size exceeds 12MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`;
      setUploadError(errorMsg);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
      return;
    }

    // Validate file type (common document/image types)
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = "Please upload a valid document (PDF, Word, Excel, Image, or Text file).";
      setUploadError(errorMsg);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
      return;
    }

    try {
      setLocalUploading(true);
      setUploadError('');
      
      if (onUploadStart) {
        onUploadStart(documentType);
      }

      const formData = new FormData();
      formData.append('document', file);
      formData.append('user', userId);
      formData.append('doc_type', getDocumentTypeDisplayName(documentType));

      // Use provider-docs endpoint instead of student-docs
      const response = await axios.post('/accounts/provider-docs/', formData);

      if (response.status === 201) {
        if (onDocumentUploaded) {
          onDocumentUploaded(documentType, file.name);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = "Upload failed. Please try again.";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.document) {
          errorMessage = Array.isArray(error.response.data.document) 
            ? error.response.data.document[0] 
            : error.response.data.document;
        }
      }
      
      setUploadError(errorMessage);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
    } finally {
      setLocalUploading(false);
    }
  }, [userId, documentType, onDocumentUploaded, onDocumentUploadFailed, onUploadStart, getDocumentTypeDisplayName]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]; // Only handle the first file
      uploadFile(file);
    }
  }, [uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    },
    multiple: false,
    disabled: localUploading || isUploading
  });

  const isCurrentlyUploading = localUploading || isUploading;
  const hasUploadedFile = uploadedFilenames && uploadedFilenames.has(documentType);
  
  return (
    <div className="col-12">
      <div className={`card h-100 ${hasError ? 'border-danger' : hasUploadedFile ? 'border-success' : 'border-secondary'}`}>
        <div 
          {...getRootProps()} 
          className={`card-body d-flex flex-column ${isCurrentlyUploading ? 'uploading' : ''} ${isDragActive ? 'drag-active' : ''}`}
          style={{ 
            cursor: isCurrentlyUploading ? 'not-allowed' : 'pointer',
            backgroundColor: hasUploadedFile ? '#f8f9fa' : 'white',
            transform: hasUploadedFile ? 'translateY(-2px)' : 'translateY(0px)',
            boxShadow: hasUploadedFile ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          <input {...getInputProps()} />
          <div 
            className="text-center flex-grow-1 d-flex flex-column justify-content-center"
            style={{ 
              backgroundColor: hasUploadedFile ? '#f8f9fa' : 'white',
              transform: hasUploadedFile ? 'translateY(-2px)' : 'translateY(0px)',
              boxShadow: hasUploadedFile ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            {isCurrentlyUploading ? (
              <React.Fragment>
                <div 
                  className="mb-3"
                  style={{ 
                    backgroundColor: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="spinner-border text-primary" role="status" style={{ fontSize: '1.5rem' }}>
                    <span className="visually-hidden">Uploading...</span>
                  </div>
                </div>
                <div className="fw-bold mb-2">Uploading...</div>
                <div className="text-muted small">Please wait while your document is being uploaded</div>
              </React.Fragment>
            ) : hasUploadedFile ? (
              <React.Fragment>
                <div 
                  className="mb-3"
                  style={{ 
                    backgroundColor: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="fw-bold mb-2">{title}</div>
                <div className="text-muted small">Drag & drop a file here, or click to browse</div>
                <div 
                  className="text-muted" 
                  style={{ 
                    fontSize: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  PDF, Word, Excel, Images (max 12MB)
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div className="mb-3">
                  <i className={`bi ${hasError ? 'bi-exclamation-triangle-fill text-danger' : 'bi-cloud-upload text-secondary'}`} style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="fw-bold mb-2">{title}</div>
                <div className="text-muted small">
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop a file here, or click to browse'}
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  PDF, Word, Excel, Images (max 12MB)
                </div>
              </React.Fragment>
            )}
          </div>

          {(hasUploadedFile || uploadError) && (
            <div className="mt-3">
              {hasUploadedFile && (
                <ul className="list-unstyled mb-0">
                  <li 
                    className="d-flex align-items-center"
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <small 
                      className="text-success"
                      style={{ 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {uploadedFilenames.get(documentType)}
                    </small>
                  </li>
                </ul>
              )}
              
              {uploadError && (
                <div className="text-danger small mt-2">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {uploadError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderDragDropUpload;